import type { ColDef, GridOptions, SortModelItem } from 'ag-grid-enterprise';
import { get, isEqual } from 'lodash';

import type { Asset } from '@/pages/Assets/Assets.types';
import { padIp, sortMixedIpString, sortString } from '@/utils/Common.utils';
import {
  assetNameRenderer,
  criticalityRenderer,
  dataProviderRenderer,
  dateTimeRenderer,
  hasAgentRenderer,
  riskScoreRenderer,
  textRenderer
} from '@/utils/GridRenderer.utils';

export const ASSETS_TABLE_DEFAULT_SORT_MODEL: SortModelItem[] = [
  {
    colId: 'id',
    sort: 'desc'
  }
];

const getColumnDefs = (): ColDef<Asset>[] => [
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'id'
    },
    field: 'id',
    headerName: 'ID',
    headerTooltip: 'ID',
    maxWidth: 100,
    minWidth: 70,
    sortable: true
  },
  {
    cellClass: 'ag-cell-center',
    cellRenderer: riskScoreRenderer,
    field: 'risk_score',
    headerClass: 'ag-header-center',
    headerName: 'Risk Score',
    headerTooltip: 'Risk Score',
    maxWidth: 120,
    minWidth: 120,
    sortable: true,
    width: 150
  },
  {
    cellRenderer: assetNameRenderer,
    comparator: (a, b) => {
      return sortMixedIpString(a.asset_name, b.asset_name);
    },
    equals: (a, b) =>
      a.asset_name === b.asset_name &&
      isEqual(
        a.host_isolation_exclusion_reasons,
        b.host_isolation_exclusion_reasons
      ),
    field: 'asset_name',
    flex: 3,
    headerName: 'Asset Name',
    headerTooltip: 'Asset Name',
    sortable: true,
    valueGetter: (params) => ({
      asset_name: params.data?.asset_name,
      host_isolation_exclusion_reasons: get(
        params.data,
        'host_isolation_exclusion_reasons'
      )
    })
  },
  {
    cellRenderer: dataProviderRenderer,
    field: 'data_provider',
    headerName: 'Data Provider',
    headerTooltip: 'Data Provider',
    maxWidth: 175,
    minWidth: 125,
    sortable: true,
    valueFormatter: (params) =>
      Array.isArray(params.value) ? params.value.join(', ') : params.value,
    width: 125
  },
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'asset_subtype'
    },
    field: 'asset_subtype',
    flex: 1,
    headerName: 'Type',
    headerTooltip: 'Type',
    sortable: true,
    width: 150
  },
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'ip_address'
    },
    comparator: (a: string, b: string) => {
      return sortString(padIp(a), padIp(b));
    },
    field: 'ip_address',
    headerName: 'IP Address',
    headerTooltip: 'IP Address',
    maxWidth: 170,
    minWidth: 140,
    sortable: true
  },
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'operating_system'
    },
    comparator: (a: string, b: string) => sortString(a || '', b || ''),
    field: 'operating_system',
    flex: 1,
    headerName: 'Operating System',
    headerTooltip: 'Operating System',
    sortable: true
  },
  {
    cellClass: 'ag-cell-center',
    cellRenderer: hasAgentRenderer,
    field: 'has_agent',
    headerClass: 'ag-header-center',
    headerName: 'Has Agent',
    headerTooltip: 'Has Agent',
    maxWidth: 120,
    minWidth: 120,
    sortable: true,
    valueGetter: (params) => {
      const hasAgent = params.data?.has_agent;
      return Array.isArray(hasAgent) && hasAgent.length > 0 ? 'Yes' : 'No';
    }
  },
  {
    cellClass: 'ag-cell-center',
    cellRenderer: criticalityRenderer,
    comparator: (a: string | null, b: string | null) => {
      const criticalityOrder = [null, 'Low', 'Medium', 'High', 'Critical'];
      const aIndex = criticalityOrder.indexOf(a);
      const bIndex = criticalityOrder.indexOf(b);
      return aIndex - bIndex;
    },
    field: 'criticality',
    headerClass: 'ag-header-center',
    headerName: 'Criticality',
    headerTooltip: 'Criticality',
    sortable: true,
    width: 150
  },
  {
    cellRenderer: dateTimeRenderer,
    field: 'last_seen_at',
    headerName: 'Last Seen',
    headerTooltip: 'Last Seen',
    maxWidth: 180,
    minWidth: 150,
    sortable: true
  },
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'organization_name'
    },
    field: 'organization_name',
    headerName: 'Organization',
    headerTooltip: 'Organization',
    sortable: true
  }
];

export const getAssetColumnDefs = (orgLabel: string) => {
  return getColumnDefs().map((col) => {
    if (col.field === 'organization_name') {
      return { ...col, hide: orgLabel !== 'all' };
    }
    return col;
  });
};

export const ASSET_GRID_OPTIONS: GridOptions = {
  animateRows: false,
  defaultColDef: {
    resizable: true,
    sortable: true,
    suppressHeaderMenuButton: true
  },
  pagination: true,
  rowHeight: 55,
  rowSelection: {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: false
  },
  suppressCellFocus: true,
  suppressMultiSort: true,
  tooltipShowMode: 'whenTruncated',
  suppressContextMenu: true
};
